import { useTranslation } from "react-i18next";
import { CommissionContract } from "../../../api/ComissionContractsAPI";
import { CommissionContractOfferDto } from "../../../api/CommissionContractOffersAPI";
import SectionTitle from "../../SectionTitle/SectionTitle";
import InputShortText from "../../InputShortText/InputShortText";
import SelectInput from "../../SelectInput/SelectInput";
import CustomDatePicker from "../../CustomDatePicker/CustomDatePicker";
import Notice from "../../Notice/Notice";
import { CommissionStatus } from "../../../types/commission-statuses";
import { commissionStatusToTranslationKey } from "../../../utils/commission-status";
import { isValidText } from "../../../shared/validation";

interface OrderTabSectionProps {
  form: CommissionContract;
  commission: CommissionContract;
  isEditing: boolean;
  offerDisplayName?: string;
  enabledOffers?: CommissionContractOfferDto[];
  showOfferChangeHint?: boolean;
  showPdfNotice: boolean;
  onInputChange: (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onEndDateChange: (date: Date | null) => void;
  onSelectOffer?: (offerId: number) => void;
}

export default function OrderTabSection({
  form,
  commission,
  isEditing,
  offerDisplayName,
  enabledOffers = [],
  showOfferChangeHint = false,
  showPdfNotice,
  onInputChange,
  onEndDateChange,
  onSelectOffer,
}: OrderTabSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <SectionTitle text={t("store_commission_panel.order_data_title") || "Order data"} />
      {showPdfNotice && (
        <Notice
          pillText={t("store_commission_panel.pdf_notice.pill") || "Info"}
          tinyText={
            t("store_commission_panel.pdf_notice.message") ||
            "The customer won't be able to download the PDF unless the request is accepted."
          }
        />
      )}
      <div className="grid grid-cols-1 gap-2 mb-2">
        {isEditing ? (
          <SelectInput
            label={t("store_commission_panel.status") || "Status"}
            values={Object.values(CommissionStatus).map((v) => ({
              value: v,
              readableValue: t(commissionStatusToTranslationKey(v)) || v,
            }))}
            defaultValue={form.status}
            onChange={onInputChange("status")}
          />
        ) : (
          <InputShortText
            label={t("store_commission_panel.status") || "Status"}
            defaultValue={
              t(commissionStatusToTranslationKey(form.status as CommissionStatus)) || form.status
            }
            onChange={() => {}}
            isDisabled
          />
        )}
      </div>
      {enabledOffers.length > 0 && isEditing && onSelectOffer ? (
        <div className="grid grid-cols-1 gap-2 mb-2">
          <SelectInput
            label={t("store_commission_panel.selected_offer") || "Package"}
            values={enabledOffers.map((o) => ({
              value: String(o.id),
              readableValue: o.name,
            }))}
            defaultValue={
              form.commissionContractOfferId != null ? String(form.commissionContractOfferId) : ""
            }
            onChange={(e) => onSelectOffer(parseInt(e.target.value, 10))}
          />
          {showOfferChangeHint && (
            <p className="text-sm text-gray-500 -mt-1">
              {t("store_commission_panel.offer_change_hint") ||
                "Changing the package does not update existing payment requests. Review the payment plan manually."}
            </p>
          )}
        </div>
      ) : (
        offerDisplayName && (
          <div className="grid grid-cols-1 gap-2 mb-2">
            <InputShortText
              label={t("store_commission_panel.selected_offer") || "Selected package"}
              defaultValue={offerDisplayName}
              onChange={() => {}}
              isDisabled
            />
          </div>
        )
      )}
      <div className="grid grid-cols-1 gap-2 mb-2">
        <CustomDatePicker
          label={t("store_commission_panel.end_date") || "End date"}
          value={
            isEditing
              ? form.endDate
                ? new Date(form.endDate)
                : null
              : commission.endDate
                ? new Date(commission.endDate)
                : null
          }
          onChange={onEndDateChange}
          isDisabled={!isEditing}
          dateFormat="dd/MM/yyyy"
          showTimeSelect={false}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <InputShortText
          label={t("store_commission_panel.contract_number") || "Contract number"}
          defaultValue={isEditing ? form.contractNumber || "" : commission.contractNumber || ""}
          onChange={isEditing ? onInputChange("contractNumber") : () => {}}
          isDisabled={!isEditing}
          validationFn={(value) => !!value && isValidText(value, 0, 50)}
          errorMessage="errors.long_short"
        />
        <InputShortText
          label={t("store_commission_panel.contract_reference") || "Contract reference"}
          defaultValue={isEditing ? form.contractReference || "" : commission.contractReference || ""}
          onChange={isEditing ? onInputChange("contractReference") : () => {}}
          isDisabled={!isEditing}
          validationFn={(value) => !!value && isValidText(value, 0, 50)}
          errorMessage="errors.long_short"
        />
      </div>
    </div>
  );
}
